import { makeStyles } from '@mui/styles';
import { COLORS } from '../../../EatClubTheme';

const FONT_GROTESQUE = "'General Grotesque', Inter, 'Helvetica', 'sans-serif'";

/** Figma Portal Vision — Chip/Small (node 20:4830); shared by section headers and nav rows */
const NEW_LABEL_CHIP = {
  boxSizing: 'border-box',
  color: '#313131',
  textTransform: 'uppercase',
  backgroundColor: '#FCFCCF',
  fontSize: '12px',
  fontWeight: 400,
  display: 'inline-flex',
  height: '24px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px',
  letterSpacing: '2px',
  borderRadius: '6px',
  border: '1px solid #7C7815',
  boxShadow: '0px 4px 2px rgba(224, 224, 224, 0.25)',
  lineHeight: '12px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontFamily: FONT_GROTESQUE,
};

const useStyles = makeStyles((theme) => ({
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    backgroundColor: '#F5F2ED',
    height: '100%',
    maxHeight: '100%',
    margin: 0,
    padding: '0px 0 20px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  /** Wraps scroll + bottom fade so list fades into footer (LINEN matches drawer / sidebar) */
  navScrollOuter: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  navScrollBottomFade: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: '14px',
    height: '32px',
    pointerEvents: 'none',
    zIndex: 2,
    background: `linear-gradient(to top, ${COLORS.LINEN} 0%, ${COLORS.LINEN}00 100%)`,
  },
  /** Main nav scroll area — no reserved gutter; thin scrollbar only when needed */
  navScrollBody: {
    flex: 1,
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarGutter: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(49, 49, 49, 0.22) rgba(0,0,0,0)',
    paddingBottom: '36px',
    '@supports (overflow: overlay)': {
      overflowY: 'overlay',
    },
    '&:hover': {
      scrollbarColor: 'rgba(49, 49, 49, 0.35) rgba(0,0,0,0)',
    },
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(49, 49, 49, 0.12)',
      borderRadius: 8,
      minHeight: 40,
    },
    '&:hover::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(49, 49, 49, 0.28)',
    },
  },
  list: {
    padding: '0 20px',
    marginTop: '0',
    width: '100%',
    boxSizing: 'border-box',
  },
  navColumnBlock: {
    width: '100%',
  },
  navSectionItems: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingTop: '24px',
  },
  subItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  expandArrow: {
    color: theme.colors.BLACK,
    transitionDuration: '0.1s',
  },
  hideNavigationContainer: {
    padding: '12px',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  divider: {
    margin: '30px 0',
    borderColor: '#ECE6DC',
  },
  versionNumber: {
    opacity: '40%',
    ...theme.eatclub.typography.body.sm,
    color: theme.colors.NIMBUS,
    padding: '0 20px 0',
    boxSizing: 'border-box',
    marginTop: '0',
  },
  footerBlock: {
    marginTop: '0',
    flexShrink: 0,
  },
  navigationLink: {
    padding: '0 12px',
    paddingLeft: '12px',
    height: '36px',
    minHeight: '36px',
    borderRadius: '12px',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.1s',
    fontWeight: 500,
    fontSize: '12px',
    color: '#5A5A59',
    textTransform: 'uppercase',
    textDecoration: 'none',
    boxSizing: 'border-box',
    border: '1px solid transparent',

    '&:hover': {
      backgroundColor: '#EEEAE3',
      color: '#000000',
    },

    '&:focus-visible': {
      outline: '2px solid #313131',
      outlineOffset: '-2px',
    },
  },
  subNavigationLink: {
    paddingLeft: '12px',
    paddingRight: '12px',
    height: '36px',
    minHeight: '36px',
    borderRadius: '8px',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    gap: '10px',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'flex-start',
    transition: 'all 0.1s',
    marginTop: '8px',
    fontSize: '16px',
    fontWeight: 400,
    color: theme.colors.smoke || '#6E6E6D',
    textDecoration: 'none',
    boxSizing: 'border-box',
    border: '1px solid transparent',

    '&:hover': {
      backgroundColor: '#EDE9E2',
    },

    '&:focus-visible': {
      outline: '2px solid #313131',
      outlineOffset: '-2px',
    },
  },
  staffAlert: {
    background: theme.colors.STAFF_BLUE,
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  navItemLabel: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: '12px',
    whiteSpace: 'nowrap',
    letterSpacing: '1.2px',
  },
  menuIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    width: '24px',
    height: '24px',
    color: 'inherit',
    flexShrink: 0,
    '& svg': {
      width: '16px',
      height: '16px',
      display: 'block',
      marginTop: '1px', // to visually center the icon
    },
  },
  /** Figma Portal Vision — Chip/Small: nav row NEW/BETA (pushes to the end of the row) */
  newLabel: {
    ...NEW_LABEL_CHIP,
    marginLeft: 'auto',
  },
  /** Same chip as newLabel, for section column headers (no auto margin) */
  newLabelSection: {
    ...NEW_LABEL_CHIP,
  },
  /** Figma: ~20px vertical space around inter-section rules */
  columnSectionBorder: {
    width: 'auto',
    height: '1px',
    backgroundColor: '#ECE6DC',
    margin: '18px 10px',
    flexShrink: 0,
  },
  /** Line under restaurant switcher */
  navTopDivider: {
    width: 'auto',
    height: '1px',
    backgroundColor: '#ECE6DC',
    margin: '16px 30px',
    flexShrink: 0,
  },
  restaurantSwitcherWrap: {
    padding: '0 20px 0',
    boxSizing: 'border-box',
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '10px 12px 0',
    marginTop: 0,
    width: '100%',
    boxSizing: 'border-box',
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sectionHeaderLabel: {
    fontFamily: FONT_GROTESQUE,
    /* Figma: General Grotesque Book for section headers */
    fontWeight: 500,
    fontSize: '12px',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: '1.2px',
    color: '#0C0C0C',
    textTransform: 'uppercase',
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '40px',
    padding: '6px 12px',
    marginBottom: '6px',
    borderRadius: '8px',
    textDecoration: 'none',
    boxSizing: 'border-box',
    transition: 'background-color 0.15s, box-shadow 0.15s, color 0.15s',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.42)',
      color: '#0C0C0C',
    },
    '&:focus-visible': {
      outline: '2px solid #313131',
      outlineOffset: '2px',
    },
  },
  navRowInactive: {
    position: 'relative',
    zIndex: 0,
    color: '#6E6D6D',
    backgroundColor: '#F5F2ED',
    boxShadow: 'none',
  },
  navRowSelected: {
    position: 'relative',
    zIndex: 1,
    color: '#0C0C0C',
    backgroundColor: 'rgba(255, 255, 255, 0.79)',
    boxShadow: '0 1px 2px rgba(174, 174, 174, 0.08), 0 2px 10px -2px rgba(174, 174, 174, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
  },
  navRowInner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    minHeight: '24px',
  },
  navRowLabel: {
    fontFamily: FONT_GROTESQUE,
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  createOfferWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '0 12px 6px 12px',
    marginBottom: '6px',
    marginTop: '6px',
    boxSizing: 'border-box',
  },
  createOfferNavLink: {
    display: 'inline-flex',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  createOfferButtonLabel: {
    display: 'inline-block',
    marginTop: '-1px',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    marginLeft: '4px',
    border: '1px solid #000',
    borderRadius: '8px',
    backgroundColor: '#FCFCCF',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
    textTransform: 'uppercase',
    width: '52px',
    height: '25px',
  },
  tagText: {
    marginTop: '-2px',
    letterSpacing: '1.2px',
    color: '#313131',
  },
}));

export const navigationButtonStyles = {
  /* Figma Portal Vision — Button/Standard (e.g. node 20:4903) in sidenav */
  createOffer: {
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      width: 'auto',
      minWidth: 0,
      minHeight: '40px',
      height: '40px',
      backgroundColor: 'transparent',
      fontFamily: FONT_GROTESQUE,
      fontSize: '16px',
      lineHeight: '18px',
      fontWeight: 400,
      letterSpacing: 0,
      color: '#313131',
      padding: '10px 24px',
      borderRadius: '100px',
      border: '1.5px solid #313131',
      boxShadow: 'none',
      filter: 'none',
    },
    buttonHover: {
      backgroundColor: 'rgba(49, 49, 49, 0.03)',
      color: '#313131',
      filter: 'none',
      boxShadow: 'none',
    },
  },
};

export default useStyles;
